import React, { useState, useEffect, useRef } from "react";
import "moment/locale/pt-br";
import { useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';

import Layout from "~/components/UI/Layout/Layout";
import List from "./List";
import { loadMalhaFina } from "./actions";
import { LoadingTopBar } from "~/components/UI/LoadingTopBar";
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  getMalhaFinaFiltersKey,
  getDefaultMalhaFinaFilters,
  normalizeMalhaFinaFilters,
} from './helpers';

const MalhaFinaScreen = ({ match }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { department } = match.params;

  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);

  // Filters kept across refreshes for a few minutes, one entry per department.
  const persisted = usePersistedFilters(getMalhaFinaFiltersKey(department), getDefaultMalhaFinaFilters);
  const applied = normalizeMalhaFinaFilters(persisted.filters, persisted.defaults);
  const year = applied.year;

  // Loads the restored (or default) year on mount and whenever it changes.
  useEffect(() => {
    loadData();
  }, [year]);

  async function loadData() {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route (department) only, so it only ever
    // holds the default year; using it for another year would flash wrong data.
    const usesCache = year === persisted.defaults.year;

    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.malhaFina,
          dispatch,
          pathname
        });
      }
      const { payload } = await dispatch(
        loadMalhaFina({
          department,
          year,
          showLoading: false,
        })
      );
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: {
            department,
            data: payload.data[department]
          }
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <List persisted={persisted} applied={applied} />
      </Layout>
    </div>
  );
};

// Remounts per department so each one reads its own saved filters (and the
// load/cache logic starts fresh) when switching departments in the menu.
export const ScreensMalhaFina = (props) => (
  <MalhaFinaScreen key={props.match.params.department} {...props} />
);
